import { cn } from "@/utils";
import {
  BarChart3,
  FileBarChart,
  FileText,

  LayoutDashboard,
  MessageSquare,
  Play,
  Settings,
  Users,
  Video,
  X,
} from "lucide-react";
import React, { useEffect } from "react";
import { NavLink } from "react-router-dom";
import { useCaseContext } from "../../contexts/CaseContext";

// Global pages (not case-specific)
const globalNavigation = [
  {
    name: "Dashboard",
    href: "/",
    icon: LayoutDashboard,
  },
  {
    name: "Cases",
    href: "/cases",
    icon: FileText,
  },
  {
    name: "Focus Groups",
    href: "/focus-groups",
    icon: Video,
  },
  {
    name: "Settings",
    href: "/settings",
    icon: Settings,
  },
];

// Case-specific pages (always visible, contextual to selected case)
const caseSpecificNavigation = [
  {
    name: "Jurors",
    href: "/jurors",
    icon: Users,
  },
  {
    name: "Simulations",
    href: "/simulations",
    icon: Play,
  },
  {
    name: "Analytics",
    href: "/analytics",
    icon: BarChart3,
  },
  {
    name: "Juror Interviews",
    href: "/chat",
    icon: MessageSquare,
  },
  {
    name: "Reports",
    href: "/reports",
    icon: FileBarChart,
  },
];

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const { selectedCase, setSelectedCase, availableCases, loadingCases } =
    useCaseContext();

  const formatCaseName = (caseName: string) => {
    // Truncate long case names for dropdown display
    return caseName.length > 25 ? caseName.substring(0, 25) + "..." : caseName;
  };

  const handleCaseSelection = (value: string) => {
    if (value === "add-new") {
      // Navigate to Cases page to add new case
      setSelectedCase(null);
      window.location.href = "/cases";
      onClose(); // Close mobile menu
    } else {
      const case_ = availableCases.find((c) => c.id === value);
      setSelectedCase(case_ || null);
      onClose(); // Close mobile menu
    }
  };

  // Close sidebar on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      // Prevent body scroll when sidebar is open on mobile
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose]);

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div
        className={cn(
          "w-64 bg-white/5 backdrop-blur-xl border-r border-white/10 flex flex-col h-full transition-transform duration-300 ease-in-out",
          "lg:translate-x-0", // Always visible on desktop
          "fixed lg:relative z-50 lg:z-auto", // Fixed on mobile, relative on desktop
          isOpen ? "translate-x-0" : "-translate-x-full", // Slide in/out on mobile
        )}
      >
        {/* Mobile Close Button */}
        <div className="lg:hidden flex justify-end p-4">
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-6 pt-4 lg:pt-12 pb-6 space-y-3">
          {/* Global Navigation */}
          {globalNavigation.map((item) => (
            <NavLink
              key={item.name}
              to={item.href}
              onClick={() => onClose()} // Close sidebar on mobile when clicking link
              className={({ isActive }) =>
                cn(
                  "group flex items-center px-3 py-2.5 text-sm font-medium rounded-xl transition-all duration-300",
                  isActive
                    ? "bg-white/[0.08] backdrop-blur-md border-l-2 border-l-blue-400 border border-white/[0.12] shadow-[inset_0_1px_1px_rgba(255,255,255,0.06),0_0_12px_rgba(59,130,246,0.08)] text-white"
                    : "text-gray-300 hover:bg-white/[0.04] hover:text-white border-l-2 border-transparent",
                )
              }
            >
              {({ isActive }) => (
                <>
                  <item.icon
                    className={cn(
                      "mr-3 h-5 w-5 transition-all duration-300",
                      isActive
                        ? "text-blue-400 drop-shadow-[0_0_4px_rgba(59,130,246,0.4)]"
                        : "text-gray-400 group-hover:text-white",
                    )}
                  />
                  {item.name}
                </>
              )}
            </NavLink>
          ))}

          {/* Clean Separator */}
          <div className="border-t border-white/10 pt-4 mt-6">
            {/* Direct Case Selector */}
            <div className="px-3 py-2 mb-3">
              <select
                value={selectedCase?.id || ""}
                onChange={(e) => handleCaseSelection(e.target.value)}
                disabled={loadingCases}
                className="w-full px-3 py-2 text-sm bg-white/10 border border-white/20 rounded-lg backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-primary-500 text-white"
              >
                {loadingCases ? (
                  <option>Loading cases...</option>
                ) : (
                  <>
                    {availableCases.length === 0 && (
                      <option value="">No cases available</option>
                    )}
                    {availableCases.map((case_) => (
                      <option key={case_.id} value={case_.id}>
                        {formatCaseName(case_.name)}
                      </option>
                    ))}
                    <option value="add-new">+ Add Case</option>
                  </>
                )}
              </select>
            </div>

            {/* Case-Specific Navigation (Always Visible, Indented) */}
            <div className="space-y-1">
              {caseSpecificNavigation.map((item) => (
                <NavLink
                  key={item.name}
                  to={item.href}
                  className={({ isActive }) =>
                    cn(
                      "group flex items-center pl-6 pr-3 py-2.5 text-sm font-medium rounded-xl transition-all duration-300 ml-3",
                      !selectedCase
                        ? "opacity-50 cursor-not-allowed text-gray-400"
                        : isActive
                          ? "bg-white/[0.08] backdrop-blur-md border-l-2 border-l-blue-400 border border-white/[0.12] shadow-[inset_0_1px_1px_rgba(255,255,255,0.06),0_0_12px_rgba(59,130,246,0.08)] text-white"
                          : "text-gray-300 hover:bg-white/[0.04] hover:text-white border-l-2 border-transparent",
                    )
                  }
                  onClick={(e) => {
                    if (!selectedCase) {
                      e.preventDefault();
                    } else {
                      onClose(); // Close sidebar on mobile when clicking link
                    }
                  }}
                >
                  {({ isActive }) => (
                    <>
                      <item.icon
                        className={cn(
                          "mr-3 h-4 w-4 transition-all duration-300",
                          !selectedCase
                            ? "text-gray-500"
                            : isActive
                              ? "text-blue-400 drop-shadow-[0_0_4px_rgba(59,130,246,0.4)]"
                              : "text-gray-400 group-hover:text-white",
                        )}
                      />
                      {item.name}
                    </>
                  )}
                </NavLink>
              ))}

              {!selectedCase && (
                <div className="px-6 py-2 text-xs text-gray-500 text-center">
                  Select a case above to access contextual pages
                </div>
              )}
            </div>
          </div>
        </nav>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-white/10">
          {selectedCase && (
            <div className="mb-3 p-2 bg-white/5 rounded-lg">
              <div className="text-xs text-gray-400">Active Case:</div>
              <div
                className="text-xs text-white font-medium truncate"
                title={selectedCase.name}
              >
                {formatCaseName(selectedCase.name)}
              </div>
            </div>
          )}
          <div className="text-xs text-gray-400 text-center">
            <p>Version 1.0.0</p>
            <p className="mt-1">© 2026 GenAI Jury</p>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
